import { useState, useEffect } from 'react';
import { WifiOff, Loader2 } from 'lucide-react';
import api from '../api/axios';

export default function NetworkIndicator() {
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);
    const handleServerError = () => setIsOffline(true);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('server-error', handleServerError);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('server-error', handleServerError);
    };
  }, []);

  useEffect(() => {
    let interval;
    if (isOffline) {
      interval = setInterval(async () => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          
          const response = await fetch((import.meta.env.VITE_API_URL || '') + '/auth/verify', {
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          
          // 401 is fine (means server is up but we have no token in this simple fetch).
          // 502, 504 mean it's still down/restarting.
          if (response.status >= 500) {
            throw new Error('Server still down');
          }
          
          setIsOffline(false);
          window.location.reload(); 
        } catch (e) {
          // Seguimos sin conexión (Network Error or 500+)
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isOffline]);

  if (!isOffline) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center pointer-events-auto animate-fade-in duration-200">
      <div className="bg-slate-900 border border-rose-500/50 rounded-2xl p-8 max-w-md w-full text-center shadow-[0_0_50px_rgba(225,29,72,0.2)] flex flex-col items-center">
        <div className="w-20 h-20 rounded-full bg-rose-500/20 flex items-center justify-center mb-6 animate-pulse">
          <WifiOff size={40} className="text-rose-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Sin Conexión al Servidor</h2>
        <p className="text-slate-300 mb-6 leading-relaxed">
          Se ha perdido la conexión con el servidor o se está aplicando una actualización. Por seguridad, no puede realizar cambios hasta que se restablezca.
        </p>
        <div className="flex items-center gap-3 text-rose-300 bg-rose-500/10 px-6 py-3 rounded-xl border border-rose-500/20">
          <Loader2 size={18} className="animate-spin" />
          <span className="font-medium">Reconectando automáticamente...</span>
        </div>
      </div>
    </div>
  );
}
